import type { Observation } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { recordedObservationAnswers } from '@seen/core/observationReferences'
import { cn } from '@seen/ui'
import {
  IconAlertTriangle,
  IconArrowsDiagonal,
  IconEraser,
  IconForms,
  IconNote,
  IconSettings,
} from '@tabler/icons-react'
import {
  type JSX,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { ConditionalBranchIcon } from '#src/components/rich-text-editor/conditional-display/ConditionalBranchIcon.tsx'
import { ObservationAnswerSetDialog } from '#src/components/rich-text-editor/ObservationNode/ObservationAnswerSetDialog.tsx'
import { actionButtonClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationDialogContent } from '#src/components/rich-text-editor/ObservationNode/ObservationDialogContent.tsx'
import { ObservationFrameEngagement } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import { ObservationHistoryPopover } from '#src/components/rich-text-editor/ObservationNode/ObservationHistoryPopover.tsx'
import { ObservationNodeCondition } from '#src/components/rich-text-editor/ObservationNode/observationNodeCondition.ts'
import {
  ObservationNodeConfigure,
  ObservationNodeConfiguring,
} from '#src/components/rich-text-editor/ObservationNode/observationNodeConfig.ts'
import { ObservationWrapper } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useClearableRecords } from '#src/components/rich-text-editor/ObservationNode/useClearableRecords.ts'
import { useClearObservationAnswer } from '#src/components/rich-text-editor/ObservationNode/useClearObservationAnswer.ts'
import {
  focusPrimary,
  onControl,
  useFrameEngagement,
} from '#src/components/rich-text-editor/ObservationNode/useFrameEngagement.ts'
import { useObservationParticipant } from '#src/components/rich-text-editor/ObservationNode/useObservationParticipant.ts'
import { useRovingToolbar } from '#src/components/rich-text-editor/ObservationNode/useRovingToolbar.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useGroupedObservationHistory } from '#src/hooks/useObservationHistory.ts'

export interface ObservationNodeFrameProps {
  attributes: ObservationNodeAttributes
  // Defaults to the node's coding display.
  label?: string
  // A template marks the question as needing an answer; defaults to the
  // node's own mark.
  required?: boolean
  // The variant's value control(s).
  children: ReactNode
  // Shown after the controls, before the details button.
  trailing?: ReactNode
  className?: string
  noHover?: boolean
  wrapperRef?: Ref<HTMLSpanElement>
  // Makes the line a button, worked by a click, Enter or Space. Ignored when
  // `details` is 'click', where those open the details.
  onClick?: () => void
  // How a recorded observation reaches its details: a button at the end of the
  // line (behind the structured capture flag), a click anywhere on the line,
  // or not at all, as inside the details themselves.
  details?: 'button' | 'click' | 'none'
  onDetailsOpenChange?: (open: boolean) => void
  // Whether the details offer the value's editor too.
  valueInDetails?: boolean
  // The node's own, where the note can be changed. With it a recorded
  // observation can be cleared back to unanswered.
  updateAttributes?: (attributes: Partial<ObservationNodeAttributes>) => void
  // 'block' gives the node a line of its own, the value flowing under and
  // after the label with its line breaks kept: for free text, whose text is
  // the answer. Everything else sits in the sentence.
  layout?: 'inline' | 'block'
  // Whose history the node offers; defaults to the editor's participant.
  participant?: Observation['subject']
  // The node's record is another participant's (pasted from their note, or
  // the participant changed since): it can be cleared here, not changed.
  foreign?: boolean
  // Why the node cannot record now (no participant chosen), shown over its
  // disabled controls.
  blockedReason?: string
  // Offered among the node's actions, for a node whose click does something
  // else (a template's instrument, which a click configures).
  openForm?: () => void
}

// The one box every observation node renders in: the label, the variant's
// controls, and the way to the details dialog once there is an observation.
export function ObservationNodeFrame({
  attributes,
  label = attributes.codingDisplay,
  required = !!attributes.required,
  children,
  trailing,
  className,
  noHover,
  wrapperRef,
  onClick,
  details = 'button',
  onDetailsOpenChange,
  valueInDetails = true,
  updateAttributes,
  layout = 'inline',
  participant,
  foreign = false,
  blockedReason,
  openForm,
}: ObservationNodeFrameProps): JSX.Element {
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const isReadOnly = useIsReadOnly()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsFocus, setDetailsFocus] = useState<'note'>()
  const boxRef = useRef<HTMLSpanElement | null>(null)
  const {
    focused,
    openPopovers,
    settleFocus,
    reportPopoverOpen,
    onFocus,
    onBlur,
  } = useFrameEngagement(boxRef)
  const configuring = useContext(ObservationNodeConfiguring)
  // Engaged while focus is within the node's own box, while a popover or the
  // details dialog opened from it is open, or while the configuration sheet
  // (which takes focus from it) is open on it.
  const engaged = focused || openPopovers.size > 0 || detailsOpen || configuring
  const [noteSlot, setNoteSlot] = useState<'empty' | 'filled' | null>(null)
  const [noteRequested, setNoteRequested] = useState(false)
  // An empty note asked for and left empty folds away again with the node.
  useEffect(() => {
    if (!engaged && noteSlot !== 'filled') {
      setNoteRequested(false)
    }
  }, [engaged, noteSlot])
  const editorParticipant = useObservationParticipant()
  const subject = participant ?? editorParticipant ?? undefined
  const offersActions = inlineCapture && details === 'button'
  const answerReferences = recordedObservationAnswers(attributes.references)
  // A repeating choice's answers held in the note, before any is recorded.
  const heldAnswers = attributes.repeats ? (attributes.drafts ?? []) : []
  const isAnswerSet =
    attributes.repeats &&
    (answerReferences.length > 0 || heldAnswers.length > 0)
  // The concept's history, this node's own record included: one record is
  // already a history of one. A multi-select capture is one row.
  const history = useGroupedObservationHistory({
    participant: offersActions && !foreign ? subject : undefined,
    coding: { system: attributes.system, code: attributes.code },
    limit: 5,
  }).flatMap((group) => group.members)
  const setBox = useCallback(
    (element: HTMLSpanElement | null): void => {
      boxRef.current = element
      if (typeof wrapperRef === 'function') {
        wrapperRef(element)
      } else if (wrapperRef) {
        wrapperRef.current = element
      }
    },
    [wrapperRef]
  )
  // The strip is one Tab stop; its actions are reached with the arrows.
  const toolbar = useRovingToolbar<HTMLSpanElement>()
  const clearAnswer = useClearObservationAnswer()
  const resetNode =
    updateAttributes && !isReadOnly
      ? () =>
          updateAttributes(
            attributes.repeats
              ? { references: null, groupId: null, drafts: null }
              : attributes.draft
                ? { reference: null, draft: null }
                : { reference: null }
          )
      : undefined
  const clearable = useClearableRecords(attributes, !!resetNode)
  // The answer is held in the note, not in the chart: nothing to confirm
  // removing from it.
  const heldDraft = attributes.repeats
    ? heldAnswers.length > 0 && answerReferences.length === 0
    : !!attributes.draft

  const detailsMode =
    (!attributes.reference && !attributes.draft && !isAnswerSet) ||
    (details === 'button' && !inlineCapture)
      ? 'none'
      : details

  const changeDetailsOpen = useCallback(
    (open: boolean): void => {
      setDetailsOpen(open)
      onDetailsOpenChange?.(open)
      if (!open) {
        settleFocus()
      }
    },
    [onDetailsOpenChange, settleFocus]
  )
  const hasDetailsButton = detailsMode === 'button' && !foreign
  const openDetails = useCallback(
    (focus?: 'note'): void => {
      setDetailsFocus(focus)
      changeDetailsOpen(true)
    },
    [changeDetailsOpen]
  )
  const offersHistory = offersActions && !foreign && history.length > 0
  const [historyOpen, setHistoryOpen] = useState(false)
  const changeHistoryOpen = useCallback(
    (open: boolean): void => {
      setHistoryOpen(open)
      reportPopoverOpen(open, 'history')
    },
    [reportPopoverOpen]
  )
  const engagement = useMemo(
    () => ({
      setPopoverOpen: reportPopoverOpen,
      openDetails: hasDetailsButton ? openDetails : undefined,
      reportNoteSlot: setNoteSlot,
      noteRequested,
      openHistory: offersHistory ? () => changeHistoryOpen(true) : undefined,
    }),
    [
      hasDetailsButton,
      openDetails,
      reportPopoverOpen,
      noteRequested,
      offersHistory,
      changeHistoryOpen,
    ]
  )
  const canClear = !!(resetNode && (heldDraft || clearable.length > 0))
  const canAddNote =
    noteSlot === 'empty' && !noteRequested && !isReadOnly && !foreign
  const configure = useContext(ObservationNodeConfigure)
  // In a template, a conditional node words its condition in a glyph just
  // before its frame, part of the node however its line wraps.
  const condition = useContext(ObservationNodeCondition)
  const conditionTitle = condition
    ? `Shows when: ${condition.text}${condition.cycle ? ' (refers to itself)' : ''}`
    : undefined
  const openConfigure = offersActions && !foreign ? configure : null
  const showsActions =
    offersActions &&
    (hasDetailsButton ||
      offersHistory ||
      canAddNote ||
      canClear ||
      !!openForm ||
      !!openConfigure)
  const activate = detailsMode === 'click' ? () => openDetails() : onClick

  return (
    <>
      {/* Before the frame, as a conditional span's glyph is before its text. */}
      {condition && (
        <button
          type="button"
          data-condition-chip=""
          title={conditionTitle}
          aria-label={conditionTitle}
          disabled={!configure}
          onClick={(event) => {
            event.stopPropagation()
            configure?.()
          }}
          className={cn(
            'mr-0.5 flex items-center self-center disabled:cursor-default',
            condition.cycle
              ? 'text-red-600'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <ConditionalBranchIcon />
        </button>
      )}
      <ObservationWrapper
        ref={setBox}
        // Every child is one 20px line tall, so the row is as tall as a
        // vitals row; aligned to the start, a note that wraps keeps the label
        // on its first line. The named group lets the actions and an empty
        // note show only while the node is engaged or hovered.
        className={cn(
          'group/observation relative items-start pr-px',
          // Exactly one 20px line, like the note's text around it.
          inlineCapture && 'rounded-sm py-0',
          // Behind the flag, like the rest of structured capture.
          // A block node hugs its text, up to the line's width, so an empty
          // one is only as wide as its label and placeholder.
          inlineCapture &&
            layout === 'block' &&
            'min-w-0 max-w-full whitespace-pre-wrap',
          activate &&
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950',
          className
        )}
        noHover={noHover}
        engaged={engaged}
        configuring={configuring}
        onFocus={onFocus}
        onBlur={onBlur}
        // A press on the label or the ground, not on a control, takes the
        // node. Its mousedown is prevented in the capture phase, before
        // ProseMirror's own handling would move the note's caret or select
        // the node, and before the browser would focus the note instead.
        // The node then acts on the click, once the gesture is over, so
        // nothing later in it can undo the focus or close what it opened.
        onMouseDownCapture={(event) => {
          if (!onControl(event.currentTarget, event.target)) {
            event.preventDefault()
          }
        }}
        onClick={(event) => {
          if (onControl(event.currentTarget, event.target)) {
            return
          }
          if (activate) {
            activate()
          } else {
            focusPrimary(event.currentTarget)
          }
        }}
        // A line that opens something is a button, reachable with Tab and
        // worked with Enter or Space like a click.
        role={activate ? 'button' : undefined}
        tabIndex={activate ? 0 : undefined}
        onKeyDown={
          activate
            ? (event) => {
                if (
                  event.target === event.currentTarget &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault()
                  activate()
                }
              }
            : undefined
        }
      >
        <span className={cn(inlineCapture && 'leading-5')}>
          {label}
          {required && (
            <span className="ml-0.5 text-destructive" title="Required">
              *<span className="sr-only"> (required)</span>
            </span>
          )}
          :&nbsp;
        </span>
        {/* A frame inside this node (a part, its details) is not the node. */}
        <ObservationNodeConfiguring.Provider value={false}>
          <ObservationFrameEngagement.Provider value={engagement}>
            {blockedReason ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so the reason the controls are off can be read
                    tabIndex={0}
                    className="inline-flex min-w-0 flex-wrap items-start [&_button]:pointer-events-none"
                  >
                    {children}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{blockedReason}</TooltipContent>
              </Tooltip>
            ) : (
              children
            )}
            {trailing}
          </ObservationFrameEngagement.Provider>
        </ObservationNodeConfiguring.Provider>
        {foreign && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="img"
                aria-label="Recorded for another participant"
                className="ml-1 flex items-center self-center text-amber-600"
              >
                <IconAlertTriangle className="size-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Recorded for another participant</TooltipContent>
          </Tooltip>
        )}
        {showsActions && (
          // A small toolbar just above the box, right-aligned: never over the
          // node's own controls, taking no room in the line and moving
          // nothing as it appears. Transparent rather than hidden while the
          // node is not engaged, so Tab still reaches it and reveals it.
          // biome-ignore lint/a11y/noStaticElementInteractions: only keeps its buttons' clicks and keys from also working the line
          <span
            data-observation-actions=""
            ref={toolbar.ref}
            role="toolbar"
            aria-label={`${label} actions`}
            onFocus={toolbar.onFocus}
            // Its buttons must not also work the line underneath.
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation()
              toolbar.onKeyDown(event)
            }}
            className="pointer-events-none absolute right-0 bottom-full z-10 mb-0.5 flex items-center gap-0.5 rounded-xs border border-border bg-popover opacity-0 shadow-xs transition-opacity group-data-[engaged=true]/observation:pointer-events-auto group-data-[engaged=true]/observation:opacity-100"
          >
            {canAddNote && (
              <ActionButton
                label="Add note"
                onClick={() => setNoteRequested(true)}
              >
                <IconNote className="size-3.5" />
              </ActionButton>
            )}
            {offersHistory && (
              <ObservationHistoryPopover
                attributes={attributes}
                subject={subject}
                history={history}
                open={historyOpen}
                onOpenChange={changeHistoryOpen}
                onOpenDetails={
                  hasDetailsButton ? () => openDetails() : undefined
                }
              />
            )}
            {canClear && resetNode && (
              <ActionButton
                label="Clear answer"
                onClick={() =>
                  heldDraft
                    ? // Nothing is in the chart yet, so there is nothing to confirm.
                      resetNode()
                    : clearAnswer({
                        observation: clearable[0],
                        observations: attributes.repeats
                          ? clearable
                          : undefined,
                        label,
                        onCleared: resetNode,
                      })
                }
              >
                <IconEraser className="size-3.5" />
              </ActionButton>
            )}
            {hasDetailsButton && (
              <ActionButton
                label="Details and history"
                onClick={() => openDetails()}
              >
                <IconArrowsDiagonal className="size-3.5" />
              </ActionButton>
            )}
            {openForm && (
              <ActionButton label="Open form" onClick={openForm}>
                <IconForms className="size-3.5" />
              </ActionButton>
            )}
            {openConfigure && (
              <ActionButton label="Configure" onClick={openConfigure}>
                <IconSettings className="size-3.5" />
              </ActionButton>
            )}
          </span>
        )}
      </ObservationWrapper>
      {/* Outside the box, so a click in the dialog does not reach its onClick. */}
      {detailsMode !== 'none' && (
        <Dialog open={detailsOpen} onOpenChange={changeDetailsOpen}>
          <ObservationNodeConfiguring.Provider value={false}>
            <DialogDisplay
              // Opened from the note, the note editor takes focus itself.
              onOpenAutoFocus={(event) => {
                if (detailsFocus === 'note') {
                  event.preventDefault()
                }
              }}
            >
              {attributes.repeats ? (
                <ObservationAnswerSetDialog
                  attributes={attributes}
                  initialFocus={detailsFocus}
                  updateAttributes={updateAttributes}
                  onCleared={inlineCapture ? resetNode : undefined}
                />
              ) : (
                <ObservationDialogContent
                  initialFocus={detailsFocus}
                  attributes={attributes}
                  showValue={valueInDetails}
                  // Behind the flag, like the clear button itself.
                  onCleared={inlineCapture ? resetNode : undefined}
                />
              )}
            </DialogDisplay>
          </ObservationNodeConfiguring.Provider>
        </Dialog>
      )}
    </>
  )
}

function ActionButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className={actionButtonClass()}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
